import React from "react";

import { Button } from "@portex-pro/ui-components";
import { QuoteType } from "api/types/generated-types";
import StepCollapse from "app/pages/quote-request/_components/StepCollapse";
import { useCreateStepCollapseProps } from "app/pages/quote-request/_hooks/useStepCollapse";
import { useQuoteRequestSelector } from "app/pages/quote-request/_store";
import { quoteRequestActions } from "app/pages/quote-request/_store/quoteRequestSliceV2";
import { QuoteStepIndex } from "app/pages/quote-request/types";
import clsx from "clsx";
import xor from "lodash/xor";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FinalSchema } from "../../_schemas";
import { CarrierRoutingPreferences } from "./CarrierRoutingPreferences";
import { QuotePreferenceCard } from "./QuotePreferenceCard";

interface QuotePreferencesStepTagProps {
  quoteTypes: QuoteType[];
  directSailingRequired: boolean;
  capacityGuaranteeRequired: boolean;
}

const QuotePreferencesStepTag: React.FC<QuotePreferencesStepTagProps> = ({
  quoteTypes,
  directSailingRequired,
  capacityGuaranteeRequired,
}) => {
  const { t } = useTranslation(["common", "quoteRequest"]);

  const preferences = [];

  // Add quote types
  if (quoteTypes?.includes(QuoteType.Cheapest)) {
    preferences.push(t("common:cheapestQuote"));
  }
  if (quoteTypes?.includes(QuoteType.Fastest)) {
    preferences.push(t("common:fastestQuote"));
  }
  if (quoteTypes?.includes(QuoteType.ForwarderPreference)) {
    preferences.push(t("common:forwarderPreference"));
  }

  // Add additional preferences
  if (directSailingRequired) {
    preferences.push(t("quoteRequest:QuotePreferencesStep.needDirectSailing"));
  }
  if (capacityGuaranteeRequired) {
    preferences.push(t("quoteRequest:QuotePreferencesStep.needCapacityGuarantee"));
  }

  if (preferences.length === 0) {
    return null;
  }

  const displayPreferences = preferences.slice(0, 2);
  const excessCount = preferences.length - 2;

  return (
    <div className="flex items-center space-x-1 text-blue-600 font-bold text-sm">
      <span>{displayPreferences.join(", ")}</span>
      {excessCount > 0 && <span>+{excessCount}</span>}
    </div>
  );
};

export const QuotePreferencesStep: React.FC<{ onSubmit: (data: FinalSchema) => void }> = ({ onSubmit }) => {
  const { t } = useTranslation(["common", "quoteRequest"]);
  const { baseStepCollapseProps, setIsOpen } = useCreateStepCollapseProps({
    expectedStepIndex: QuoteStepIndex.Service,
    name: t("common:quotePreferences"),
    number: 2,
  });
  const setStepIndex = quoteRequestActions.useSetStepIndex();
  const setAirQuoteRequest = quoteRequestActions.useSetQuoteRequest();
  const { stepIndex } = useQuoteRequestSelector((state) => {
    return state.quoteRequestSliceV2;
  });

  const methods = useFormContext<FinalSchema>();

  // Watch form values for the tag
  const quoteTypes = useWatch({
    control: methods.control,
    name: "quoteTypes",
  });
  const directSailingRequired = useWatch({
    control: methods.control,
    name: "directSailingRequired",
  });
  const capacityGuaranteeRequired = useWatch({
    control: methods.control,
    name: "capacityGuaranteeRequired",
  });

  const submit = (data: FinalSchema) => {
    onSubmit(data);
    setStepIndex(QuoteStepIndex.Containers);
    setIsOpen(false);
  };

  return (
    <StepCollapse
      {...baseStepCollapseProps}
      tag={
        <QuotePreferencesStepTag
          quoteTypes={quoteTypes || []}
          directSailingRequired={directSailingRequired || false}
          capacityGuaranteeRequired={capacityGuaranteeRequired || false}
        />
      }
      footer={
        stepIndex <= QuoteStepIndex.Service ? (
          <div className="flex justify-end mt-2 w-full">
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={methods.handleSubmit(submit)}
              style={{ width: 150 }}
            >
              {t("quoteRequest:RoutingStep.nextButton")}
            </Button>
          </div>
        ) : null
      }
    >
      <div className="space-y-6 mt-4 max-w-[840px]">
        {/* Quote Types Section */}
        <div
          className={clsx(
            "border-2 rounded-lg overflow-hidden",
            methods.watch("quoteTypes")?.length > 0 ? "border-blue-500" : "border-gray-200"
          )}
        >
          <Controller
            name="quoteTypes"
            control={methods.control}
            render={({ field }) => (
              <QuotePreferenceCard
                type="cheapest"
                title={t("common:cheapestQuote")}
                description={t("quoteRequest:QuotePreferencesStep.requestForCheapest")}
                checked={field.value.includes(QuoteType.Cheapest)}
                onChange={() => {
                  setAirQuoteRequest({ load_spec: { quote_types: xor(field.value ?? [], [QuoteType.Cheapest]) } });
                }}
                position="top"
              />
            )}
          />

          <Controller
            name="quoteTypes"
            control={methods.control}
            render={({ field }) => (
              <QuotePreferenceCard
                type="fastest"
                title={t("common:fastestQuote")}
                description={t("quoteRequest:QuotePreferencesStep.requestForFastest")}
                checked={field.value.includes(QuoteType.Fastest)}
                onChange={() => {
                  setAirQuoteRequest({ load_spec: { quote_types: xor(field.value ?? [], [QuoteType.Fastest]) } });
                }}
                position="middle"
              />
            )}
          />

          <Controller
            name="quoteTypes"
            control={methods.control}
            render={({ field }) => (
              <QuotePreferenceCard
                type="forwarder"
                title={t("common:forwarderPreference")}
                description={t("quoteRequest:QuotePreferencesStep.letYourFreight")}
                checked={field.value.includes(QuoteType.ForwarderPreference)}
                onChange={() => {
                  setAirQuoteRequest({
                    load_spec: { quote_types: xor(field.value ?? [], [QuoteType.ForwarderPreference]) },
                  });
                }}
                position="bottom"
              />
            )}
          />
        </div>

        {/* Additional Options Section */}
        <div className="space-y-4">
          <Controller
            name="directSailingRequired"
            control={methods.control}
            render={({ field }) => (
              <QuotePreferenceCard
                type="direct-sailing"
                title={t("quoteRequest:QuotePreferencesStep.needDirectSailing")}
                description={t("quoteRequest:QuotePreferencesStep.requestForDirectSailing")}
                checked={field.value || false}
                onChange={(checked: boolean) => setAirQuoteRequest({ load_spec: { direct_sailing_required: checked } })}
                position="standalone"
              />
            )}
          />

          <Controller
            name="capacityGuaranteeRequired"
            control={methods.control}
            render={({ field }) => (
              <QuotePreferenceCard
                type="capacity-guarantee"
                title={t("quoteRequest:QuotePreferencesStep.needCapacityGuarantee")}
                description={t("quoteRequest:QuotePreferencesStep.requestForCapacity")}
                checked={field.value || false}
                onChange={(checked: boolean) =>
                  setAirQuoteRequest({ load_spec: { capacity_guarantee_required: checked } })
                }
                position="standalone"
              />
            )}
          />
        </div>

        {/* Carrier and Routing Preferences Section */}
        <Controller
          name="carrierRoutingPrefNotes"
          control={methods.control}
          render={({ field }) => (
            <CarrierRoutingPreferences
              value={field.value || ""}
              onChange={(value) => setAirQuoteRequest({ carrier_routing_pref_notes: value })}
            />
          )}
        />

        {/* Validation Error */}
        {methods.formState.errors.quoteTypes && (
          <div className="text-red-600 text-sm">
            {t("quoteRequest:QuotePreferencesStep.atLeastOneQuoteTypeRequired")}
          </div>
        )}
      </div>
    </StepCollapse>
  );
};
